import React from 'react';
import { fixtureGlyphs } from '../lib/fixtureEmoji';

/* SeatingFloorView: a read-only floor layout for picking a table, with geometry mirroring FloorPlanDesigner. */

const CHAIR_GAP = 28;

const FIXTURE_TINT = {
  kitchen: '#fde68a', washroom: '#bae6fd', stairs: '#e2e8f0', plant: '#bbf7d0',
  door: '#ddd6fe', window: '#dbeafe', column: '#cbd5e1', reception: '#fbcfe8',
  bar: '#fed7aa', wall: '#e2e8f0',
};

function chairsAround(t) {
  const n = Math.max(0, t.capacity || 0);
  if (n === 0) return [];
  const cx = CHAIR_GAP + (t.width || 100) / 2;
  const cy = CHAIR_GAP + (t.height || 70) / 2;
  const w = t.width || 100;
  const h = t.height || 70;
  if (t.shape === 'round') {
    const r = Math.max(w, h) / 2 + 14;
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return { left: cx + r * Math.cos(a) - 8, top: cy + r * Math.sin(a) - 8 };
    });
  }
  // rectangle/square: round-robin distribute across the 4 sides
  const edges = ['top', 'right', 'bottom', 'left'];
  const placements = [];
  for (let i = 0; i < n; i++) placements.push({ edge: edges[i % 4] });
  const perEdge = { top: 0, right: 0, bottom: 0, left: 0 };
  placements.forEach((p) => { perEdge[p.edge] += 1; });
  const seen = { top: 0, right: 0, bottom: 0, left: 0 };
  return placements.map((p) => {
    const slot = seen[p.edge]++;
    const total = perEdge[p.edge];
    const fraction = (slot + 1) / (total + 1);
    if (p.edge === 'top') return { left: CHAIR_GAP + w * fraction - 8, top: CHAIR_GAP - 22 };
    if (p.edge === 'bottom') return { left: CHAIR_GAP + w * fraction - 8, top: CHAIR_GAP + h + 6 };
    if (p.edge === 'left') return { left: CHAIR_GAP - 22, top: CHAIR_GAP + h * fraction - 8 };
    return { left: CHAIR_GAP + w + 6, top: CHAIR_GAP + h * fraction - 8 };
  });
}

function tableLabel(t) {
  return t.table_code || `#${t.id}`;
}

// Per-status visuals. Only `available` tables can be selected to start an order.
export const TABLE_STATUS_STYLE = {
  available:   { table: 'linear-gradient(135deg,#059669,#047857)', chair: '#cbd5e1', selectable: true,  label: 'Available' },
  occupied:    { table: 'linear-gradient(135deg,#b91c1c,#7f1d1d)', chair: '#fecaca', selectable: false, label: 'Occupied' },
  reserved:    { table: 'linear-gradient(135deg,#d97706,#b45309)', chair: '#fed7aa', selectable: false, label: 'Reserved' },
  unavailable: { table: 'linear-gradient(135deg,#64748b,#475569)', chair: '#cbd5e1', selectable: false, label: 'Unavailable' },
};

function TableNode({ item, status, selected, onSelect }) {
  const isRound = item.shape === 'round';
  const w = item.width || 100;
  const h = item.height || 70;

  const style = TABLE_STATUS_STYLE[status] || TABLE_STATUS_STYLE.available;
  const selectable = style.selectable;
  const tableBg = selected ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : style.table;
  const chairBg = selected ? '#bfdbfe' : style.chair;

  return (
    <div
      onClick={() => selectable && onSelect(item)}
      className={`absolute select-none ${selectable ? 'cursor-pointer' : 'cursor-not-allowed'} ${selected ? 'z-20' : 'z-10'}`}
      style={{
        left: (item.x ?? 100) - CHAIR_GAP,
        top: (item.y ?? 100) - CHAIR_GAP,
        width: w + CHAIR_GAP * 2,
        height: h + CHAIR_GAP * 2,
        transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
        opacity: selectable || selected ? 1 : 0.85,
      }}
      title={`${tableLabel(item)} · ${item.capacity} seats · ${style.label}`}
    >
      {chairsAround(item).map((c, i) => (
        <div
          key={i}
          className="absolute rounded-md"
          style={{ left: c.left, top: c.top, width: 16, height: 16, background: chairBg, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
        />
      ))}
      <div
        className={`absolute flex flex-col items-center justify-center text-white font-semibold transition-shadow ${selected ? 'ring-2 ring-blue-400' : ''} ${selectable ? 'hover:brightness-110' : ''}`}
        style={{
          left: CHAIR_GAP,
          top: CHAIR_GAP,
          width: w,
          height: h,
          background: tableBg,
          borderRadius: isRound ? '50%' : 8,
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          fontSize: Math.min(14, h * 0.24),
        }}
      >
        <span>{tableLabel(item)}</span>
        <span className="text-[10px] font-normal opacity-90">{item.capacity} seats</span>
      </div>
    </div>
  );
}

export default function SeatingFloorView({
  section, statusMap = {}, tableBusy = {}, selectedTableId, onSelect, zoom = 1,
}) {
  if (!section) {
    return <div className="text-sm text-muted-foreground p-6">No section selected.</div>;
  }
  const tables = section.tables || [];
  const fixtures = section.fixtures || [];
  const width = section.canvas_width || 1200;
  const height = section.canvas_height || 800;
  const statusOf = (t) =>
    statusMap[t.id] || (tableBusy[t.id] ? 'occupied' : 'available');

  return (
    <div className="h-full w-full overflow-auto p-6 bg-muted/40">
      {/* Zoom wrapper: the outer box reserves the scaled footprint so scrollbars match what's on screen. */}
      <div
        className="mx-auto"
        style={{ width: width * zoom, height: height * zoom }}
      >
      <div
        className="relative rounded-lg border border-border shadow-inner"
        style={{
          width,
          height,
          transform: zoom === 1 ? undefined : `scale(${zoom})`,
          transformOrigin: 'top left',
          backgroundColor: 'hsl(var(--card))',
          // A floor grid over the themed card, readable in both palettes.
          backgroundImage: [
            'repeating-linear-gradient(0deg, hsl(var(--border)) 0 1px, transparent 1px 40px)',
            'repeating-linear-gradient(90deg, hsl(var(--border)) 0 1px, transparent 1px 40px)',
          ].filter(Boolean).join(', '),
        }}
      >
        {/* Fixtures (context only, non-interactive). Each wears its emoji —
            🪴, 🚪, 🪟 — repeated along a strip so a window lining a wall reads
            as panes; the same face every plan renderer shows. */}
        {fixtures.map((f) => {
          const w = f.width || 100;
          const h = f.height || 80;
          const glyphs = fixtureGlyphs(f.fixture_type, w, h);
          const vertical = h > w;
          const thin = Math.max(1, Math.min(w, h));
          return (
            <div
              key={`fx-${f.id}`}
              className={`absolute flex ${vertical ? 'flex-col' : 'flex-row'} items-center justify-center gap-0.5 overflow-hidden rounded-md text-[10px] text-slate-600 select-none`}
              style={{
                left: f.x ?? 100,
                top: f.y ?? 100,
                width: w,
                height: h,
                background: f.color || FIXTURE_TINT[f.fixture_type] || '#e2e8f0',
                transform: f.rotation ? `rotate(${f.rotation}deg)` : undefined,
                opacity: 0.7,
              }}
              title={f.label || f.fixture_type}
            >
              {glyphs.map((g, i) => (
                <span key={i} aria-hidden="true" className="leading-none"
                  style={{ fontSize: glyphs.length > 1
                    ? Math.min(20, Math.max(10, Math.round(thin * 0.75)))
                    : Math.min(24, Math.max(12, Math.round(thin * 0.5))) }}>
                  {g}
                </span>
              ))}
              {Math.min(w, h) > 30 && <span>{f.label || f.fixture_type}</span>}
            </div>
          );
        })}

        {/* Tables */}
        {tables.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            No tables in this section.
          </div>
        ) : (
          tables.map((t) => (
            <TableNode
              key={t.id}
              item={t}
              status={statusOf(t)}
              selected={selectedTableId === t.id}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
      </div>
    </div>
  );
}
